import React, { useContext, useMemo, useState, useEffect } from 'react';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import { constructGallery } from '@jetshop/ui/Gallery/constructGallery';
import { Above } from '@jetshop/ui/Breakpoints';
import Image from '@jetshop/ui/Image/Image';
import { cx } from 'linaria';
import Video from '../../../components/Video/Video';

const array_move = (arr, old_index, new_index) => {
  if (new_index >= arr.length) {
    var k = new_index - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  return arr; // for testing
};

const Gallery = props => {
  const {
    sizes = [1, 1, 1, 1 / 2, 1 / 2],
    fullScreenSizes = [1, 1, 1, 1],
    thumbnailSizes = ['78px'],
    aspect = '1:1',
    thumbnailPosition = 'left',
    thumbnailQuality,
    quality,
    images,
    galleryRef,
    eagerlyLoad = [0],
    video,
    mainimages
  } = props;

  const {
    theme: { breakpoints }
  } = useShopConfig();
  const { selectedChannel } = useContext(ChannelContext);

  const [isFullscreen, setFullscreen] = useState(false);

  //Fixes thumbnail order on refreshing the page or direct links.
  //-------
  const [showThumbnails, setThumbnails] = useState(true);

  useEffect(() => {
    setTimeout(() => setThumbnails(false), 1);
    setTimeout(() => setThumbnails(true), 1);
  }, []);
  //-------

  const baseUrl = selectedChannel?.url ? String(selectedChannel.url) : null;

  const galleryImages = useMemo(() => {
    let constructedImages = [
      ...constructGallery(
        images,
        fullScreenSizes,
        aspect,
        baseUrl,
        breakpoints
      ).map(image => ({
        item: {
          ...image
        },
        video: {
          isVideo: false,
          original: ''
        }
      })),
      ...((Array.isArray(video) &&
        video?.map(videoUrl => {
          const videoId = videoUrl.split('?v=')[1];
          return {
            item: {
              original: `https://img.youtube.com/vi/${videoId}/0.jpg`,
              thumbnail: `https://img.youtube.com/vi/${videoId}/0.jpg`
            },
            video: { isVideo: true, original: videoUrl }
          };
        })) ||
        [])
    ];
    return constructedImages;
  }, [video, images, fullScreenSizes, aspect, baseUrl, breakpoints]);

  var j = mainimages;
  for (let i = 0; i < galleryImages.length; i++) {
    if (galleryImages[i].video.isVideo) {
      array_move(galleryImages, i, j);
      j++;
    }
  }

  return (
    <Above breakpoint="md">
      {matches => (
        <ImageGallery
          ref={r => (galleryRef ? (galleryRef.current = r) : null)}
          items={galleryImages}
          autoPlay={false}
          showPlayButton={false}
          showNav={isFullscreen}
          showThumbnails={
            matches && !isFullscreen && showThumbnails ? true : false
          }
          thumbnailPosition={matches ? thumbnailPosition : undefined}
          showBullets={!isFullscreen}
          useBrowserFullscreen={false}
          preventDefaultTouchmoveEvent={isFullscreen}
          disableThumbnailScroll={true}
          onScreenChange={el => setFullscreen(!!el)}
          renderItem={({ item, video }) => {
            if (video?.isVideo) {
              return (
                <div className="image-gallery-image">
                  <Video src={video.original} isFullscreen={isFullscreen} />
                </div>
              );
            } else {
              return (
                <div className="image-gallery-image">
                  {isFullscreen ? (
                    <img
                      src={item.original}
                      alt={item.originalAlt}
                      srcSet={item.srcSet}
                      sizes={item.sizes}
                      title={item.originalTitle}
                    />
                  ) : (
                    <>
                      <Image
                        src={item.original}
                        alt={item.originalAlt}
                        sizes={sizes}
                        title={item.originalTitle}
                        aspect={aspect}
                        quality={quality}
                        modifiedDate={item.modifiedDate}
                        critical={eagerlyLoad.includes(item.index)}
                      />
                    </>
                  )}
                </div>
              );
            }
          }}
          renderThumbInner={({ item, video }) => {
            return matches ? (
              <div
                className={cx(
                  'image-gallery-thumbnail-inner',
                  'above-md',
                  video.isVideo && 'video'
                )}
              >
                <Image
                  src={item.thumbnail}
                  alt={item.thumbnailAlt}
                  title={item.thumbnailTitle}
                  sizes={thumbnailSizes}
                  aspect={aspect}
                  quality={thumbnailQuality}
                  cover={true}
                  className="image-gallery-thumbnail-image"
                />
              </div>
            ) : null;
          }}
        />
      )}
    </Above>
  );
};

export default Gallery;
